import { Component, Inject, Optional, OnInit, OnDestroy, ViewChild, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { WhiteLabelService } from 'src/app/_service/white-label.service';
import { DeviceService } from 'src/app/_service/tv-detect.service';
import { TVNavigationService } from 'src/app/_service/tv-navigation.service';
import { TvBackButtonService, OverlayType } from 'src/app/_service/tv-back-button.service';
import { TVFocusMemoryService } from 'src/app/_service/tv-focus-memory.service';

@Component({
  selector: 'app-login-prompt',
  templateUrl: './login-prompt.component.html',
  styleUrls: ['./login-prompt.component.css']
})
export class LoginPromptComponent implements OnInit, OnDestroy {
  message: string = 'Login or Create a Free Account to continue';
  isTV: boolean = false;
  focusedIndex: number = -1;

  @ViewChild('promptContainer') promptContainer: ElementRef;
  @ViewChildren('promptBtn') promptBtns: QueryList<ElementRef>;

  private readonly OVERLAY_ID = 'login-prompt-dialog';
  private readonly REGION_ID = 'login-prompt-buttons';
  private focusSub: Subscription;
  private prevFocus: {regionId: string; index:number} | null = null;

  constructor(
    private router: Router,
    private dialogRef: MatDialogRef<LoginPromptComponent>,
    private whitelabelService: WhiteLabelService,
    private device: DeviceService,
    private tvNav: TVNavigationService,
    private tvBackButton: TvBackButtonService,
    private focusMemory:TVFocusMemoryService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: { message?: string }
  ) {
    if (data?.message) {
      this.message = data.message;
    }
    this.isTV = this.device.isTV();
  }

  ngOnInit(): void {
    if (!this.isTV) return;

    const snapshotRegionId = this.tvNav.getActiveRegionId();
    if(snapshotRegionId){
      this.prevFocus = {regionId: snapshotRegionId, index: this.tvNav.getFocusedIndex()};
    }
    this.tvNav.suppressNavigation();

    this.tvBackButton.pushOverlay({
      id: this.OVERLAY_ID,
      type: OverlayType.DIALOG,
      onClose: () => this.dismiss()
    });

    this.focusSub = this.tvNav.focus$.subscribe(focusKey => {
      if (!focusKey) {
        this.focusedIndex = -1;
        return;
      }
      if (focusKey.startsWith(this.REGION_ID + ':')) {
        this.focusedIndex = parseInt(focusKey.split(':')[1], 10);
        const btn = this.promptBtns?.toArray()[this.focusedIndex];
        if (btn) {
          btn.nativeElement.focus();
        }
      } else {
        this.focusedIndex = -1;
      }
    });

    setTimeout(() => {
      if (this.promptContainer && this.promptBtns?.length) {
        this.tvNav.registerRegion({
          id: this.REGION_ID,
          type: 'form',
          elementRef: this.promptContainer,
          focusableItems: this.promptBtns.toArray()
        });
        this.tvNav.resumeNavigation();
        this.tvNav.activateRegion(this.REGION_ID, 0);
      }
    }, 200);
  }

  ngOnDestroy(): void {
    if (this.focusSub) {
      this.focusSub.unsubscribe();
    }
    if (!this.isTV) return;
    this.tvNav.unregisterRegion(this.REGION_ID);
    this.tvBackButton.popOverlay(this.OVERLAY_ID);
    this.tvNav.resumeNavigation();
  }

  login(): void {
    this.closeDialog(true);
    localStorage.setItem('redirectAfterLogin', this.router.url);
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  dismiss(): void {
    this.closeDialog(false);
  }

  private closeDialog(skipHistoryBack: boolean): void {
    if (this.isTV) {
      this.tvNav.unregisterRegion(this.REGION_ID);
      this.tvBackButton.popOverlay(this.OVERLAY_ID, skipHistoryBack);
      this.tvNav.resumeNavigation();
      if(this.prevFocus){
        this.focusMemory.saveFocusPosition({
          regionId: this.prevFocus.regionId,
          index: this.prevFocus.index,
          timestamp: Date.now(),
          route: this.router.url,
        });
      }
    }
    this.dialogRef.close();
  }
}
